import { Link, useParams } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import { FadeUp, ParallaxImage, MaskedLines, Eyebrow } from "@/components/Reveal";
import { getProject, PROJECTS } from "@/data/content";
import NotFound from "@/pages/NotFound";

export default function ProjectDetail() {
    const { slug } = useParams();
    const project = getProject(slug);
    if (!project) return <NotFound />;

    const idx = PROJECTS.findIndex((p) => p.slug === slug);
    const next = PROJECTS[(idx + 1) % PROJECTS.length];
    const facts = [
        ["Type", project.type],
        ["Location", project.location],
        ["Status", project.status],
        ["Year", project.year],
        ["Materials", project.materials],
    ];

    return (
        <>
            <Seo
                title={`${project.name} — ${project.type}, ${project.location} | Dagaz Development`}
                description={project.description.slice(0, 150)}
                path={`/projects/${slug}`}
                image={project.image}
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }, { name: project.name, path: `/projects/${slug}` }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "CreativeWork",
                        name: project.name,
                        creator: { "@type": "Organization", name: "Dagaz Development" },
                        locationCreated: project.location,
                        dateCreated: project.year,
                        image: { "@type": "ImageObject", url: project.image },
                    },
                ]}
            />
            <section className="relative h-[72vh] md:h-[86vh] overflow-hidden" data-testid="project-hero">
                <ParallaxImage src={project.image} alt={`${project.name} — ${project.type}, ${project.location}`} className="h-full w-full" speed={8} eager />
                <div className="absolute inset-0 bg-gradient-to-t from-graphite/80 via-graphite/20 to-graphite/10" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-16 pb-12">
                    <Eyebrow dark className="mb-6">{project.status} — {project.year}</Eyebrow>
                    <MaskedLines
                        lines={[project.name]}
                        as="h1"
                        className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-chalk"
                    />
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-chalk" data-testid="project-body">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8 border-y border-graphite/15 py-8 mb-16">
                    {facts.map(([label, value]) => (
                        <div key={label}>
                            <p className="font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate">{label}</p>
                            <p className="mt-2 font-serif text-lg md:text-xl text-graphite">{value}</p>
                        </div>
                    ))}
                </div>
                <div className="grid lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-6">
                        <p className="font-serif font-light text-2xl md:text-3xl leading-snug text-graphite">{project.description}</p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-5 lg:col-start-8">
                        <p className="font-sans text-base leading-relaxed text-graphite/75">{project.detail}</p>
                        <Link
                            to="/start-a-project"
                            data-testid="project-cta-start"
                            className="group mt-10 inline-flex items-center gap-3 px-7 py-3.5 bg-forest text-chalk font-sans text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                        >
                            Start Something Similar
                            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                        </Link>
                    </FadeUp>
                </div>
            </section>

            <Link to={`/projects/${next.slug}`} data-testid="project-next" className="group block bg-forest bg-noise px-6 md:px-12 lg:px-16 py-20 border-t border-chalk/10">
                <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-fog">Next Study →</p>
                <p className="mt-4 font-serif font-light text-4xl md:text-6xl text-chalk group-hover:italic group-hover:text-gold transition-all duration-500">{next.name}</p>
            </Link>
        </>
    );
}
